import type { TUser, TAccountType } from '@/types/user'
import type { TUrlGate, TUrlAccessResult } from '@/types/claude'

export function checkWgUrlAccess(url: string, user: TUser, account: TAccountType): TUrlAccessResult {
  const path = url.split('?')[0].split('#')[0]
  const gate = gates.find((g) => g.pattern.test(path))

  if (!gate) {
    return {
      allowed: false,
      reason: "I can't open that page from here. Try asking another way.",
      kind: 'unknown',
    }
  }

  if (gate.check && !gate.check(user, account)) {
    return {
      allowed: false,
      reason: gate.reason ?? 'This feature is not available on your plan.',
      kind: 'no_access',
    }
  }

  return { allowed: true }
}

function hasFeature(account: TAccountType, key: keyof TAccountType): boolean {
  return Number(account?.[key]) > 0
}

const gates: TUrlGate[] = [
  // /projects
  // /projects/ebooks
  // /projects/flipbooks
  // /projects/audiobooks
  // /projects/covers
  // /projects/folders
  {
    pattern: /^\/projects(\/(ebooks|flipbooks|audiobooks|covers|folders))?\/?$/,
  },

  // /projects/printbooks
  {
    pattern: /^\/projects\/printbooks\/?$/,
    check: (_u, a) => hasFeature(a, 'printbook'),
    reason: 'Print books are not available on your plan.',
  },

  // /projects/kindlebooks
  {
    pattern: /^\/projects\/kindlebooks\/?$/,
    check: (_u, a) => hasFeature(a, 'ebook_export'),
    reason: 'Kindle books are not available on your plan.',
  },

  // /docs
  // /docs/manuscripts
  // /docs/transcriptions
  // /docs/audiobooks
  {
    pattern: /^\/docs(\/(manuscripts|transcriptions|audiobooks))?\/?$/,
  },

  // /media
  // /media/my-uploads
  // /media/collections
  // /media/favorites
  {
    pattern: /^\/media(\/(my-uploads|collections|favorites))?\/?$/,
  },

  // /creator/ebook/from-scratch
  // /creator/ebook/from-docx
  // /creator/ebook/from-gdoc
  // /creator/ebook/from-blog
  {
    pattern: /^\/creator\/ebook(\/(from-scratch|from-docx|from-gdoc|from-blog))?\/?$/,
  },

  // /creator/ebook/from-wordgenie
  {
    pattern: /^\/creator\/ebook\/from-wordgenie\/?$/,
    check: (_u, a) => hasFeature(a, 'ai_credits'),
    reason: 'AI book generation is not available on your plan.',
  },

  // /creator/ebook/youtube
  {
    pattern: /^\/creator\/ebook\/youtube\/?$/,
    check: (_u, a) => hasFeature(a, 'import_from_video'),
    reason: 'Importing from YouTube is not available on your plan.',
  },

  // /creator/ebook/from-podcast
  {
    pattern: /^\/creator\/ebook\/from-podcast\/?$/,
    check: (_u, a) => hasFeature(a, 'import_from_podcast'),
    reason: 'Importing from podcasts is not available on your plan.',
  },

  // /creator/ebook/from-pdf
  {
    pattern: /^\/creator\/ebook\/from-pdf\/?$/,
    check: (_u, a) => hasFeature(a, 'import_from_pdf'),
    reason: 'Importing from PDF is not available on your plan.',
  },

  // /creator/ebook/from-video
  {
    pattern: /^\/creator\/ebook\/from-video\/?$/,
    check: (_u, a) => hasFeature(a, 'import_from_video'),
    reason: 'Importing from video is not available on your plan.',
  },

  // /creator/printbook
  // /creator/printbook/from-scratch
  // /creator/printbook/from-docx
  // /creator/printbook/from-gdoc
  // /creator/printbook/from-blog
  {
    pattern: /^\/creator\/printbook(\/(from-scratch|from-docx|from-gdoc|from-blog))?\/?$/,
    check: (_u, a) => hasFeature(a, 'printbook'),
    reason: 'Print books are not available on your plan.',
  },

  // /creator/printbook/from-pdf
  {
    pattern: /^\/creator\/printbook\/from-pdf\/?$/,
    check: (_u, a) => hasFeature(a, 'printbook') && hasFeature(a, 'import_from_pdf'),
    reason: 'Print books from PDF are not available on your plan.',
  },

  // /creator/kindle
  // /creator/kindle/from-scratch
  // /creator/kindle/from-docx
  // /creator/kindle/from-gdoc
  {
    pattern: /^\/creator\/kindle(\/(from-scratch|from-docx|from-gdoc))?\/?$/,
    check: (_u, a) => hasFeature(a, 'ebook_export'),
    reason: 'Kindle books are not available on your plan.',
  },

  // /creator/audiobook
  // /creator/audiobook/from-scratch
  // /creator/audiobook/from-docx
  {
    pattern: /^\/creator\/audiobook(\/(from-scratch|from-docx))?\/?$/,
    check: (_u, a) => hasFeature(a, 'tts_credits'),
    reason: 'Audiobooks are not available on your plan.',
  },

  // /creator/cover
  {
    pattern: /^\/creator\/cover\/?$/,
  },

  // /creator/ebook/pdf-to-flipbook
  {
    pattern: /^\/creator\/ebook\/pdf-to-flipbook\/?$/,
    check: (_u, a) => hasFeature(a, 'flipbook_projects'),
    reason: 'Flipbooks are not available on your plan.',
  },

  // /creator/wordgenie
  {
    pattern: /^\/creator\/wordgenie\/?$/,
    check: (_u, a) => hasFeature(a, 'ai_credits'),
    reason: 'AI book generation is not available on your plan.',
  },

  // /wordgenie-pro (optional /book | /presentation output-type segment)
  {
    pattern: /^\/wordgenie-pro(\/(book|presentation))?\/?$/,
    check: (u, a) => u?.permissions?.superuser === 1 || a?.wg_pro === 1,
    reason: 'Wordgenie Pro is not available for your account.',
  },

  // /profile
  {
    pattern: /^\/profile\/?$/,
  },
]
